import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pause, Play, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button, Spinner, toast } from '@tendedloop/ui';
import { AmenityIcon } from '@/components/ui/AmenityIcon';
import { useTaskStore } from '@/stores/taskStore';

export function TaskInProgress() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('operator');

  const [elapsed, setElapsed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const {
    selectedTask,
    isLoading,
    error,
    fetchTaskById,
    updateTaskStatus,
  } = useTaskStore();

  // Fetch task if not already loaded
  useEffect(() => {
    if (id && !selectedTask) {
      fetchTaskById(id);
    }
  }, [id, selectedTask, fetchTaskById]);

  // Timer
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTogglePause = () => {
    setIsPaused(!isPaused);
  };

  const handleComplete = async () => {
    if (!id) return;
    try {
      await updateTaskStatus(id, 'COMPLETED');
      navigate('/tasks');
    } catch {
      // Error handled by store
    }
  };

  const handleEscalate = () => {
    // TODO: Show escalation modal
    toast({
      title: t('taskActions.escalate'),
      description: t('taskActions.escalationComingSoon'),
    });
  };

  const handleBack = () => {
    navigate(`/tasks/${id}`);
  };

  if (isLoading && !selectedTask) {
    return (
      <div className="flex items-center justify-center h-screen bg-primary">
        <Spinner size="lg" className="text-white" />
      </div>
    );
  }

  if (error || !selectedTask) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-primary px-6 text-center">
        <h2 className="text-xl font-semibold text-white mb-2">
          {t('taskDetail.notFound')}
        </h2>
        <p className="text-white/70 mb-6">{error || t('taskDetail.loadError')}</p>
        <Button
          variant="secondary"
          onClick={() => navigate('/tasks')}
        >
          {t('taskDetail.backToTasks')}
        </Button>
      </div>
    );
  }

  const task = selectedTask;
  const amenityTypeSlug = task.amenity.amenityType.icon || task.amenity.amenityType.name.toLowerCase();

  return (
    <div className="flex flex-col h-screen bg-primary">
      {/* Back Button */}
      <div className="px-4 pt-safe">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-white/70 hover:text-white py-4 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm">{t('taskDetail.backToDetails')}</span>
        </button>
      </div>

      {/* Task Info */}
      <div className="px-6 py-4 text-center">
        <AmenityIcon
          type={amenityTypeSlug}
          size="xl"
          className="mx-auto mb-4 bg-white/10"
        />
        <h1 className="text-xl font-semibold text-white mb-1">
          {task.amenity.name}
        </h1>
        <p className="text-white/70">
          {task.amenity.building.name}
        </p>
      </div>

      {/* Timer */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-7xl font-bold text-white mb-4 font-mono">
          {formatTime(elapsed)}
        </div>
        <p className="text-white/70 text-lg">
          {isPaused ? t('taskInProgress.paused') : t('taskInProgress.inProgress')}
        </p>
      </div>

      {/* Controls */}
      <div className="px-6 py-8 space-y-4 pb-safe">
        {/* Pause/Resume */}
        <Button
          variant="outline"
          size="lg"
          fullWidth
          onClick={handleTogglePause}
          className="bg-white/10 border-white/30 text-white hover:bg-white/20"
        >
          {isPaused ? (
            <>
              <Play className="h-5 w-5 me-2" />
              {t('taskActions.resume')}
            </>
          ) : (
            <>
              <Pause className="h-5 w-5 me-2" />
              {t('taskActions.pause')}
            </>
          )}
        </Button>

        {/* Complete */}
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          onClick={handleComplete}
          loading={isLoading}
        >
          <CheckCircle className="h-5 w-5 me-2" />
          {t('taskActions.markComplete')}
        </Button>

        {/* Escalate */}
        <button
          onClick={handleEscalate}
          className="w-full py-3 text-white/70 text-sm flex items-center justify-center gap-2 hover:text-white transition-colors"
        >
          <AlertTriangle className="h-4 w-4" />
          {t('taskActions.escalate')}
        </button>
      </div>
    </div>
  );
}
